import {
  TimelineGridPrecisionOptionValue,
  TimelinesGridPrecisionUnit,
} from '@ardoq/api-types';
import { TimeRange } from '@ardoq/graph';
import { readTimelineDomainRangeViewSetting } from '../calculations';
import {
  minGridLineDistances,
  minimumDisplayedGridUnitsForGridPrecision,
  unitQuantities,
} from '../consts';
import { getBiggestTimelineGrid, getSmallestTimelineGrid } from '../utils';
import {
  getWindowScaleAndZoomLimitsForGrid,
  isGridPrecisionOptionDisabled,
  isManualGridOption,
} from './calculations';
import { DateRangeViewSetting } from '@ardoq/api-types';
import { add, sub } from '@ardoq/date-time';

/**
 * weekStartsOn: the index of the first day of the week (0 - Sunday).
 * vaue is 1 regardless of locale, because we use ISO weeks
 */
const weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6 | undefined = 1;

/**
 * Use data domain limits if no user-defined limit specified.
 * If user-defined domain lies entirely outside of dataDomain bounds, show
 * minimum domain of the biggest grid (this is not specified by design)
 */
export const getAvailableDomain = (
  [dataDomainStart, dataDomainEnd]: [Date, Date],
  viewSettingsDomainRange?: DateRangeViewSetting
): [Date, Date] => {
  const [userDefinedStart, userDefinedEnd] = readTimelineDomainRangeViewSetting(
    viewSettingsDomainRange
  );
  const biggestGrid = getBiggestTimelineGrid();
  const increment = {
    [biggestGrid]: minimumDisplayedGridUnitsForGridPrecision[biggestGrid],
  };

  const domainStart =
    userDefinedStart ??
    (userDefinedEnd && userDefinedEnd <= dataDomainStart
      ? sub(userDefinedEnd, increment)
      : dataDomainStart);

  const domainEnd =
    userDefinedEnd ??
    (userDefinedStart && userDefinedStart >= dataDomainEnd
      ? add(userDefinedStart, increment)
      : dataDomainEnd);

  return [domainStart, domainEnd];
};

export const getArdoqDateTimeWeekStartOptions = () => ({ weekStartsOn });

const getMinimumRequiredGridDomain = (
  gridPrecision: TimelinesGridPrecisionUnit
) =>
  minimumDisplayedGridUnitsForGridPrecision[gridPrecision] *
  unitQuantities[gridPrecision];

export const getScaleLimitsForGrid = (
  gridPrecision: TimelinesGridPrecisionUnit,
  chartWidth: number,
  availableExpandedDomain: TimeRange,
  isMultiUnitGrid?: boolean
) =>
  getWindowScaleAndZoomLimitsForGrid({
    chartWidth,
    selectedGridUnitTimeSpan: unitQuantities[gridPrecision],
    minGridLineDistance: minGridLineDistances[gridPrecision],
    maxGridLinedistance:
      chartWidth / minimumDisplayedGridUnitsForGridPrecision[gridPrecision],
    availableDomain: availableExpandedDomain,
    isMultiUnitGrid,
  });

type IsGridOptionDisabledArgs = {
  chartDomainRange: TimeRange;
  gridPrecision: TimelinesGridPrecisionUnit;
};
export const isGridOptionDisabled = ({
  chartDomainRange,
  gridPrecision,
}: IsGridOptionDisabledArgs) =>
  isGridPrecisionOptionDisabled({
    chartDomainRange,
    gridPrecisionViewSettingsValue: gridPrecision,
    smallestGrid: getSmallestTimelineGrid(),
    minimumRequiredGridDomain: getMinimumRequiredGridDomain(gridPrecision),
  });

type GetGridPrecisionArgs = {
  gridPrecisionViewSettingsValue: TimelineGridPrecisionOptionValue;
  availableExpandedDomain: TimeRange;
};

/**
 * If the option is manual min grid domain > chart domain,
 * set the maximum possible grid, while respecting the minimum grid line distance constraints,
 * otherwise leave as is.
 *
 * If the option is Auto grid,
 * set the maximum possible grid, while respecting the minimum grid line distance constraints
 */
export const getGridPrecision = ({
  gridPrecisionViewSettingsValue,
  availableExpandedDomain,
}: GetGridPrecisionArgs) => {
  if (isManualGridOption(gridPrecisionViewSettingsValue)) {
    return isGridOptionDisabled({
      gridPrecision: gridPrecisionViewSettingsValue,
      chartDomainRange: availableExpandedDomain,
    })
      ? detectGridBasedOnDomainRange(availableExpandedDomain)
      : gridPrecisionViewSettingsValue;
  }

  return detectGridBasedOnDomainRange(availableExpandedDomain);
};

const detectGridBasedOnDomainRange = (chartDomainRange: TimeRange) =>
  (Object.keys(minGridLineDistances) as TimelinesGridPrecisionUnit[]).reduce(
    ({ currentGrid, chartDomainRange }, key) => {
      const acc = { currentGrid, chartDomainRange };
      const currentGridDomain = getMinimumRequiredGridDomain(currentGrid);
      const nextGridDomain = getMinimumRequiredGridDomain(key);

      if (
        isGridOptionDisabled({ gridPrecision: currentGrid, chartDomainRange })
      ) {
        // decrease if disabled
        acc.currentGrid =
          currentGridDomain < nextGridDomain ? currentGrid : key;
      } else {
        // increase if next option is not disabled
        acc.currentGrid =
          !isGridOptionDisabled({ gridPrecision: key, chartDomainRange }) &&
          nextGridDomain > currentGridDomain
            ? key
            : currentGrid;
      }

      return acc;
    },
    { currentGrid: getBiggestTimelineGrid(), chartDomainRange }
  ).currentGrid;
